import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { queryKeys } from '../../lib/queryKeys.js';
import { cn } from '../../lib/format.js';
import { useUi } from '../../stores/ui.js';
import { useSession } from '../../stores/session.js';
import { Wordmark } from './Wordmark.jsx';
import { useNavLinks } from './useNavLinks.js';

/**
 * The masthead. A black bar that never changes between worlds: logo left, a
 * flat row of links centred in uppercase, and the three icons right. No
 * dropdowns — every link goes straight to its page. Gold is the only colour in
 * it — the hover, the current page, and the bag count.
 *
 * It sticks on scroll and gains a shadow once the page has moved, so the bar
 * reads as lifted over the content rather than printed on it.
 */

/** Shared by every icon control in the bar, so their focus and hover agree. */
const iconControl =
  'grid size-10 place-items-center text-white transition-colors hover:text-brand focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const links = useNavLinks();

  const openCart = useUi((state) => state.openCart);
  const toggleMenu = useUi((state) => state.toggleMenu);
  const openSearch = useUi((state) => state.openSearch);
  const sessionStatus = useSession((state) => state.status);

  const { data: cart } = useQuery({
    queryKey: queryKeys.cart.detail(),
    queryFn: () => api.get('/cart'),
    staleTime: 30 * 1000,
  });

  // The same key the shell reads, so this is served from cache.
  const { data: settings } = useQuery({
    queryKey: queryKeys.settings.public(),
    queryFn: () => api.get('/settings/public'),
    staleTime: 15 * 60 * 1000,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const itemCount = cart?.itemCount ?? 0;
  const logo = settings?.store?.logo;
  const storeName = settings?.store?.name ?? 'Golden Era';

  return (
    <header
      className={cn(
        'sticky top-0 z-40 bg-noir text-white transition-shadow duration-300',
        scrolled && 'shadow-[0_10px_30px_-18px_rgba(0,0,0,0.7)]',
      )}
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:bg-brand focus:px-4 focus:py-2 focus:text-xs focus:text-white"
      >
        Skip to content
      </a>

      <div className="mx-auto flex h-16 max-w-(--container-content) items-center gap-2 px-4 sm:px-6 lg:h-[72px] lg:gap-6">
        <button
          type="button"
          onClick={toggleMenu}
          aria-label="Open menu"
          className={cn(iconControl, '-ml-2.5 lg:hidden')}
        >
          <svg viewBox="0 0 20 20" className="size-5" fill="currentColor" aria-hidden="true">
            <path d="M2.5 5h15v1.5h-15zm0 4.25h15v1.5h-15zm0 4.25h15V15h-15z" />
          </svg>
        </button>

        <Link
          to="/"
          className="flex h-10 shrink-0 items-center focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand"
          aria-label={`${storeName}, home`}
        >
          {logo ? (
            <img src={logo} alt="" className="h-9 w-auto max-w-[190px] object-contain lg:h-11" />
          ) : (
            <Wordmark tone="light" className="h-9 lg:h-11" />
          )}
        </Link>

        <nav aria-label="Main" className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
          {links.map((link) => {
            const active = link.isActive(location);

            return (
              <Link
                key={link.to}
                to={link.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'group relative h-[72px] items-center px-2.5 text-[13px] font-medium tracking-[0.06em] whitespace-nowrap uppercase transition-colors xl:px-3.5',
                  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand',
                  // Blogs, About and Contact give way first on a narrower
                  // laptop, so the shop links never wrap or crowd the icons.
                  link.secondary ? 'hidden xl:flex' : 'flex',
                  active ? 'text-brand' : 'text-white hover:text-brand',
                )}
              >
                {link.label}
                <Underline shown={active} hover />
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center lg:ml-0">
          <button type="button" onClick={openSearch} aria-label="Search" className={iconControl}>
            <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="9" cy="9" r="5.5" />
              <path d="m13.2 13.2 3.3 3.3" strokeLinecap="round" />
            </svg>
          </button>

          <Link
            to={sessionStatus === 'authenticated' ? '/account' : '/sign-in'}
            aria-label={sessionStatus === 'authenticated' ? 'Your account' : 'Sign in'}
            className={cn(iconControl, 'hidden sm:grid')}
          >
            <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <circle cx="10" cy="6.75" r="3.25" />
              <path d="M3.75 16.5c.9-3 3.3-4.5 6.25-4.5s5.35 1.5 6.25 4.5" strokeLinecap="round" />
            </svg>
          </Link>

          <button
            type="button"
            onClick={openCart}
            data-cart-target
            aria-label={itemCount > 0 ? `Bag, ${itemCount} items` : 'Bag, empty'}
            className={cn(iconControl, 'relative -mr-2.5')}
          >
            <svg viewBox="0 0 20 20" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4.25 6.5h11.5l-1 9.25a1.25 1.25 0 0 1-1.24 1.12H6.49a1.25 1.25 0 0 1-1.24-1.12z" />
              <path d="M7.25 8.25V6a2.75 2.75 0 0 1 5.5 0v2.25" strokeLinecap="round" />
            </svg>
            {itemCount > 0 && (
              <span
                key={itemCount}
                className="absolute top-0.5 right-0 grid min-w-[17px] place-items-center rounded-full bg-brand px-1 text-[10px] leading-[17px] font-semibold text-white motion-safe:animate-[badge-pop_320ms_cubic-bezier(0.34,1.56,0.64,1)]"
                data-numeric
              >
                {itemCount}
              </span>
            )}
          </button>
        </div>
      </div>

    </header>
  );
};

/**
 * The thin gold rule under a nav item. Drawn from the left so it reads as the
 * item being underlined, not a bar appearing beneath the header.
 */
const Underline = ({ shown, hover = false }) => (
  <span
    aria-hidden="true"
    className={cn(
      'absolute inset-x-2.5 bottom-5 h-px origin-left bg-brand transition-transform duration-300 xl:inset-x-3.5',
      shown ? 'scale-x-100' : 'scale-x-0',
      hover && !shown && 'group-hover:scale-x-100',
    )}
  />
);

export default Header;
